import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CardSkeleton } from "@/components/Skeletons";
import { Server, RefreshCw, Cpu, HardDrive, MemoryStick, Ban, Check } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function Servers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pterodactyl/servers/mine");
      setData(data);
    } catch (e) {
      toast.error(extractError(e));
      setData({ servers: [], linked: false, error: extractError(e) });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-primary mb-2">// Infrastructure</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">My servers</h1>
          <p className="text-sm text-muted-foreground mt-1">Live from your Pterodactyl panel — attach any server to a ticket for instant context.</p>
        </div>
        <Button data-testid="servers-refresh" onClick={load} variant="outline" className="gap-2"><RefreshCw className="h-3.5 w-3.5" /> Refresh</Button>
      </header>

      {loading ? (
        <div className="grid md:grid-cols-2 gap-4">{Array.from({length:2}).map((_,i)=><CardSkeleton key={i}/>)}</div>
      ) : !data.linked ? (
        <div className="p-8 rounded-lg border border-dashed border-border/60 text-center">
          <Server className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
          <div className="font-display font-bold text-lg mb-1">{data.error?.includes("permission") ? "Pterodactyl API key permission required" : "No panel account found"}</div>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {data.error?.includes("permission")
              ? "Your Application API key exists, but it needs Users: Read and Servers: Read permissions. Log into the Pterodactyl panel → Admin → Application API → edit your key → grant those two permissions → refresh."
              : "We couldn't find a Pterodactyl account with your email. Sign up on the panel with the same email or open a ticket for help."}
          </p>
          {data.error && !data.error.includes("permission") && <div className="text-xs text-destructive mt-3">{data.error}</div>}
        </div>
      ) : data.servers.length === 0 ? (
        <div className="p-8 rounded-lg border border-dashed border-border/60 text-center text-sm text-muted-foreground">
          Your account has no servers yet.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {data.servers.map((s) => (
            <div key={s.id} className="p-5 rounded-lg border border-border/60 bg-card hover:border-primary/40 transition-colors" data-testid={`server-card-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display font-bold text-lg">{s.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">{s.identifier}</div>
                </div>
                {s.suspended ? (
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded border border-destructive/40 bg-destructive/10 text-destructive flex items-center gap-1"><Ban className="h-3 w-3" /> Suspended</span>
                ) : (
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-500 flex items-center gap-1"><Check className="h-3 w-3" /> Active</span>
                )}
              </div>
              {s.description && <p className="text-xs text-muted-foreground mt-2">{s.description}</p>}
              <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
                <SpecCell icon={Cpu} label="CPU" value={`${s.limits?.cpu || 0}%`} />
                <SpecCell icon={MemoryStick} label="RAM" value={`${s.limits?.memory || 0}MB`} />
                <SpecCell icon={HardDrive} label="Disk" value={`${s.limits?.disk || 0}MB`} />
              </div>
              <div className="mt-4">
                <Button asChild size="sm" className="w-full" data-testid={`attach-server-${s.id}`}>
                  <Link to={`/tickets/new?server=${encodeURIComponent(JSON.stringify({ id: s.id, name: s.name, identifier: s.identifier, node: s.node, suspended: s.suspended }))}`}>Open a ticket for this server</Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SpecCell({ icon: Icon, label, value }) {
  return (
    <div className="p-2 rounded border border-border/60 bg-secondary/40">
      <div className="flex items-center gap-1 text-muted-foreground font-mono text-[9px] uppercase tracking-widest"><Icon className="h-3 w-3" /> {label}</div>
      <div className="mt-0.5 font-medium">{value}</div>
    </div>
  );
}
